import Matter from 'matter-js';
import React, { useEffect, useRef } from 'react';

export default function BallPit({ isColorful = false, className }) {
  const containerRef = useRef(null);

  useEffect(() => {
    // module aliases
    const Engine = Matter.Engine,
      Render = Matter.Render,
      Runner = Matter.Runner,
      Bodies = Matter.Bodies,
      Composite = Matter.Composite,
      Common = Matter.Common;

    // create an engine
    const engine = Engine.create();
    const world = engine.world;

    // create a renderer
    const container = containerRef.current;
    let h = container.clientHeight;
    let w = container.clientWidth;
    const render = Render.create({
      element: container,
      engine,
      options: {
        height: container.clientHeight,
        width: container.clientWidth,
        wireframes: false,
        background: 'none',
      },
    });
    Render.run(render);

    // create a runner
    const runner = Runner.create();
    Runner.run(runner, engine);

    // Function to brighten a color
    function brightenColor(color, amount) {
      const usePound = color[0] === '#';
      const num = parseInt(color.slice(1), 16);
      let r = (num >> 16) + amount;
      let g = ((num >> 8) & 0x00ff) + amount;
      let b = (num & 0x0000ff) + amount;
      r = r > 255 ? 255 : r;
      g = g > 255 ? 255 : g;
      b = b > 255 ? 255 : b;
      return (usePound ? '#' : '') + ((r << 16) | (g << 8) | b).toString(16).padStart(6, '0');
    }

    // add bodies
    const numberOfBalls = w * 0.075;
    const balls = [];
    const colors = isColorful ? ['#9E36F1', '#FFA12F', '#07C799', '#FF2059'] : ['#ffffff', '#ECECEC', '#F7F4F4'];

    for (let i = 0; i < numberOfBalls; i++) {
      const size = Math.random() * (16 - 12) + 12;
      const x = Common.random(size * 2, w - size * 2);
      const y = Common.random(size, 0 - size * 100);
      const color = colors[i % colors.length];
      const ball = Bodies.circle(x, y, size, {
        restitution: 0.5,
        frictionAir: 0.005,
        render: {
          visible: false, // Hide the default rendering
        },
      });
      ball.customRender = {
        size: size * 1.2, // Increase size to create overlap
        color: color,
      };
      balls.push(ball);
    }

    // create bounds with increased thickness
    const wallProperties = {
      isStatic: true,
      render: {
        visible: false,
      },
    };
    const boundaryThickness = 50;
    const leftWall = Bodies.rectangle(-boundaryThickness / 2, h / 2, boundaryThickness, 2400, wallProperties);
    const floor = Bodies.rectangle(w / 2, h + boundaryThickness / 2 + 10, 2400, boundaryThickness, wallProperties);
    const rightWall = Bodies.rectangle(w + boundaryThickness / 2, h / 2, boundaryThickness, 2400, wallProperties);

    // add all of the bodies to the world
    Composite.add(world, [...balls, leftWall, floor, rightWall]);

    // Custom rendering
    Matter.Events.on(render, 'afterRender', function () {
      const context = render.context;
      balls.forEach((ball) => {
        const radius = ball.customRender.size;

        const gradient = context.createRadialGradient(
          ball.position.x + radius / 6,
          ball.position.y - radius / 2,
          radius / 8,
          ball.position.x,
          ball.position.y,
          radius
        );
        const gradientIntensity = 50;
        gradient.addColorStop(0, brightenColor(ball.customRender.color, gradientIntensity));
        gradient.addColorStop(1, ball.customRender.color);

        context.beginPath();
        context.arc(ball.position.x, ball.position.y, radius, 0, 2 * Math.PI);
        context.fillStyle = gradient;
        context.fill();
      });
    });

    // make responsive
    const resizeMatter = () => {
      w = render.canvas.width = container.clientWidth;
      h = render.canvas.height = container.clientHeight;

      Matter.Body.setPosition(leftWall, Matter.Vector.create(-boundaryThickness / 2, h / 2));
      Matter.Body.setPosition(floor, Matter.Vector.create(w / 2, h + boundaryThickness / 2 + 10));
      Matter.Body.setPosition(rightWall, Matter.Vector.create(w + boundaryThickness / 2, h / 2));
    };

    window.addEventListener('resize', resizeMatter);

    // Function to detect if the user is on a mobile device
    const isMobile = () => {
      return /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    };

    // Add hover and touch event listeners
    const handleInteraction = (event) => {
      const mousePosition = {
        x: event.touches ? event.touches[0].clientX : event.clientX,
        y: event.touches ? event.touches[0].clientY : event.clientY,
      };

      const rect = render.canvas.getBoundingClientRect();
      mousePosition.x -= rect.left;
      mousePosition.y -= rect.top;

      balls.forEach((ball) => {
        const distance = Math.sqrt(
          Math.pow(ball.position.x - mousePosition.x, 2) + Math.pow(ball.position.y - mousePosition.y, 2)
        );

        const radius = ball.circleRadius;

        // If the mouse is within the ball's radius, apply a force to the ball
        if (distance < radius * 1.2) {
          const forceMagnitude = 0.005 * ball.mass;
          const force = {
            x: (ball.position.x - mousePosition.x) * forceMagnitude,
            y: (ball.position.y - mousePosition.y) * forceMagnitude,
          };

          Matter.Body.applyForce(ball, ball.position, force);
        }
      });
    };

    // Add event listeners for touch events on mobile devices
    if (isMobile()) {
      render.canvas.addEventListener('touchstart', handleInteraction);
      render.canvas.addEventListener('touchmove', handleInteraction);

      // Prevent default touch actions to avoid scrolling while interacting
      render.canvas.addEventListener('touchstart', (event) => {
        event.preventDefault();
      });
      render.canvas.addEventListener('touchmove', (event) => {
        event.preventDefault();
      });
    } else {
      // Add event listeners for mouse events on desktop devices
      render.canvas.addEventListener('mousemove', handleInteraction);
    }

    // Cleanup function to remove event listeners and Matter.js instances
    return () => {
      window.removeEventListener('resize', resizeMatter);
      if (isMobile()) {
        render.canvas.removeEventListener('touchstart', handleInteraction);
        render.canvas.removeEventListener('touchmove', handleInteraction);
        render.canvas.removeEventListener('touchstart', (event) => {
          event.preventDefault();
        });
        render.canvas.removeEventListener('touchmove', (event) => {
          event.preventDefault();
        });
      } else {
        render.canvas.removeEventListener('mousemove', handleInteraction);
      }

      // Destroy Matter.js instances
      Render.stop(render);
      Runner.stop(runner);
      Composite.clear(world, false);
      Engine.clear(engine);
      render.canvas.remove();
    };
  }, []);

  return <div className={className} ref={containerRef} data-analytics-section="ballpit" />;
}
